<template>
  <div v-if="showBanner" class="upcoming-maintenance-banner" :class="bannerClass">
    <div class="container">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <font-awesome-icon :icon="bannerIcon" class="me-3" size="lg" />
          <div>
            <h6 class="mb-1 fw-bold">{{ bannerTitle }}</h6>
            <p class="mb-0 small">{{ bannerMessage }}</p>
            <div class="mt-1" v-if="scheduledMaintenance">
              <small class="fw-semibold">
                <font-awesome-icon :icon="timeIcon" class="me-1" />
                {{ timeLabel }}: {{ formatMaintenanceTime(scheduledMaintenance.start) }} -
                {{ formatMaintenanceTime(scheduledMaintenance.end) }}
                <span v-if="!scheduledMaintenance.isActive" class="ms-2 text-muted">
                  (in {{ formatTimeUntilMaintenance(scheduledMaintenance.start) }})
                </span>
              </small>
            </div>
            <div class="mt-1" v-else-if="isMaintenanceMode">
              <small class="fw-semibold">
                <font-awesome-icon :icon="timeIcon" class="me-1" />
                {{ timeLabel }}
              </small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button
            class="btn btn-sm btn-outline-light"
            @click="refreshPage"
            title="Seite aktualisieren"
          >
            <font-awesome-icon icon="refresh" />
          </button>
          <button
            v-if="props.dismissible && !isMaintenanceMode"
            class="btn btn-sm btn-outline-light"
            @click="dismissBanner"
            title="Banner schließen"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { maintenanceApi } from '@/api/maintenance'

const props = defineProps({
  dismissible: {
    type: Boolean,
    default: true,
  },
})

// State
const scheduledMaintenance = ref(null)
const isMaintenanceMode = ref(false)
const maintenanceMessage = ref('')
const dismissedMaintenanceStart = ref(null)
const lastUpdated = ref(null)

// Computed
const showBanner = computed(() => {
  // Show banner for normal maintenance mode
  if (isMaintenanceMode.value) {
    return true
  }

  // Show banner for scheduled maintenance
  if (scheduledMaintenance.value) {
    // Show banner if it's upcoming and should show, or if it's active
    const shouldShow =
      scheduledMaintenance.value.shouldShowBanner || scheduledMaintenance.value.isActive

    if (!shouldShow) return false

    // Check if this maintenance period was dismissed
    if (props.dismissible && dismissedMaintenanceStart.value) {
      const currentStart = scheduledMaintenance.value.start
      const dismissedStart = dismissedMaintenanceStart.value

      // If the start dates match, don't show the banner
      if (currentStart === dismissedStart) {
        return false
      }
    }

    return true
  }

  return false
})

const bannerClass = computed(() => {
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'maintenance-active'
  }
  return 'maintenance-upcoming'
})

const bannerIcon = computed(() => {
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'tools'
  }
  return 'calendar-day'
})

const bannerTitle = computed(() => {
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'Aktueller Wartungsmodus'
  }
  return 'Geplanter Wartungsmodus'
})

const bannerMessage = computed(() => {
  // Priority: scheduled maintenance message, then normal maintenance message, then defaults
  if (scheduledMaintenance.value && scheduledMaintenance.value.message) {
    return scheduledMaintenance.value.message
  }
  if (maintenanceMessage.value) {
    return maintenanceMessage.value
  }
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'Die Anwendung befindet sich derzeit im Wartungsmodus. Es kann zu Einschränkungen kommen.'
  }
  return 'Ein Wartungsmodus ist geplant. Es kann zu Einschränkungen kommen.'
})

const timeIcon = computed(() => {
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'clock'
  }
  return 'calendar-alt'
})

const timeLabel = computed(() => {
  if (
    isMaintenanceMode.value ||
    (scheduledMaintenance.value && scheduledMaintenance.value.isActive)
  ) {
    return 'Aktiv'
  }
  return 'Geplant'
})

// Methods
const loadMaintenanceStatus = async () => {
  try {
    const response = await maintenanceApi.getStatus()

    // Store lastUpdated timestamp
    lastUpdated.value = response.lastUpdated

    // Load normal maintenance mode
    isMaintenanceMode.value = response.isMaintenanceMode || false
    maintenanceMessage.value = response.maintenanceMessage || ''

    // Load scheduled maintenance
    if (
      response.scheduledMaintenance &&
      (response.scheduledMaintenance.isUpcoming || response.scheduledMaintenance.isActive)
    ) {
      scheduledMaintenance.value = response.scheduledMaintenance
    } else {
      scheduledMaintenance.value = null
    }
    
    // Check dismissal after loading data (so lastUpdated is available)
    checkDismissal()
  } catch (error) {
    console.error('Error loading maintenance status:', error)
  }
}

const formatMaintenanceTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatTimeUntilMaintenance = (dateString) => {
  if (!dateString) return ''

  const now = new Date()
  const maintenanceDate = new Date(dateString)
  const diffMs = maintenanceDate - now

  if (diffMs <= 0) return 'jetzt'

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) {
    const remainingHours = diffHours % 24
    if (remainingHours > 0) {
      return `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'} ${remainingHours} ${remainingHours === 1 ? 'Stunde' : 'Stunden'}`
    } else {
      return `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'}`
    }
  } else if (diffHours > 0) {
    const remainingMinutes = diffMinutes % 60
    if (remainingMinutes > 0) {
      return `${diffHours} ${diffHours === 1 ? 'Stunde' : 'Stunden'} ${remainingMinutes} ${remainingMinutes === 1 ? 'Minute' : 'Minuten'}`
    } else {
      return `${diffHours} ${diffHours === 1 ? 'Stunde' : 'Stunden'}`
    }
  } else {
    // Mindestens 1 Minute anzeigen, auch wenn es 0 Minuten sind
    const minutes = Math.max(1, diffMinutes)
    return `${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`
  }
}

const refreshPage = () => {
  window.location.reload()
}

const dismissBanner = () => {
  if (scheduledMaintenance.value && scheduledMaintenance.value.start) {
    // Store the start date of the dismissed maintenance period
    dismissedMaintenanceStart.value = scheduledMaintenance.value.start

    // Store dismissal with start date and lastUpdated timestamp
    const dismissalData = {
      startDate: scheduledMaintenance.value.start,
      lastUpdated: lastUpdated.value,
      dismissedAt: new Date().toISOString(),
    }

    localStorage.setItem('maintenance-banner-dismissed', JSON.stringify(dismissalData))

    // Dispatch custom event to notify parent components
    window.dispatchEvent(new CustomEvent('maintenance-banner-dismissed'))
  }
}

const checkDismissal = () => {
  try {
    const dismissedData = localStorage.getItem('maintenance-banner-dismissed')
    if (dismissedData) {
      const parsed = JSON.parse(dismissedData)
      
      // Check if lastUpdated is newer than the dismissed timestamp
      if (lastUpdated.value && parsed.lastUpdated) {
        const currentLastUpdated = new Date(lastUpdated.value)
        const dismissedLastUpdated = new Date(parsed.lastUpdated)
        
        // If current lastUpdated is newer, reset dismissal
        if (currentLastUpdated > dismissedLastUpdated) {
          resetDismissal()
          return
        }
      }
      
      dismissedMaintenanceStart.value = parsed.startDate
    }
  } catch (error) {
    console.error('Error parsing dismissed maintenance data:', error)
    // Clear invalid data
    localStorage.removeItem('maintenance-banner-dismissed')
  }
}

const resetDismissal = () => {
  dismissedMaintenanceStart.value = null
  localStorage.removeItem('maintenance-banner-dismissed')
}

// Make resetDismissal available globally for debugging
if (typeof window !== 'undefined') {
  window.resetMaintenanceBanner = resetDismissal
}

// Lifecycle
let intervalId = null

onMounted(() => {
  loadMaintenanceStatus()

  // Check maintenance status every 30 seconds
  intervalId = setInterval(loadMaintenanceStatus, 30000)
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style scoped>
.upcoming-maintenance-banner {
  padding: 1rem 0;
  color: #212529;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.maintenance-upcoming {
  background: linear-gradient(135deg, #ffc107, #e0a800);
}

.maintenance-active {
  background: linear-gradient(135deg, var(--quitly-primary) 0%, var(--quitly-gradient-end) 100%);
  color: white;
}

.maintenance-active .btn-outline-light {
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.maintenance-active .btn-outline-light:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.8);
}

.maintenance-upcoming .btn-outline-light {
  border-color: rgba(33, 37, 41, 0.5);
  color: #212529;
}

.maintenance-upcoming .btn-outline-light:hover {
  background-color: rgba(33, 37, 41, 0.1);
  border-color: rgba(33, 37, 41, 0.8);
}
</style>
